/**
 * Chart Data Cache Utility
 *
 * Provides localStorage-based caching for chart data to improve performance
 * by loading cached data instantly and updating in the background.
 */

const CACHE_PREFIX = 'chartData_';
const CACHE_VERSION = 'v1';

/**
 * Generate a cache key for chart data
 * @param {string} chartType - Type of chart (e.g., 'accountsChart')
 * @param {string} columnKey - The selected column key
 * @returns {string} - Cache key
 */
const getCacheKey = (chartType, columnKey) => {
  return `${CACHE_PREFIX}${CACHE_VERSION}_${chartType}_${columnKey}`;
};

/**
 * Load cached chart data from localStorage
 * @param {string} chartType - Type of chart
 * @param {string} columnKey - The selected column key
 * @returns {Object|null} - Cached data or null if not found/invalid
 */
export const loadChartDataFromCache = (chartType, columnKey) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const key = getCacheKey(chartType, columnKey);
    const cached = window.localStorage.getItem(key);

    if (!cached) {
      return null;
    }

    const parsed = JSON.parse(cached);

    // Validate cache structure
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Failed to load chart data from cache:', error);
    return null;
  }
};

/**
 * Save chart data to localStorage cache
 * @param {string} chartType - Type of chart
 * @param {string} columnKey - The selected column key
 * @param {Object} data - Chart data to cache
 */
export const saveChartDataToCache = (chartType, columnKey, data) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const key = getCacheKey(chartType, columnKey);
    const payload = {
      ...data,
      cachedAt: new Date().toISOString()
    };

    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    // Silently fail to avoid breaking the UI
    console.warn('Failed to save chart data to cache:', error);
  }
};

/**
 * Clear all chart data caches
 */
export const clearAllChartDataCaches = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const keys = Object.keys(window.localStorage);
    const chartCacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

    chartCacheKeys.forEach(key => {
      window.localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear chart data caches:', error);
  }
};

/**
 * Clear cache for a specific chart type and column
 * @param {string} chartType - Type of chart
 * @param {string} columnKey - The selected column key
 */
export const clearChartDataCache = (chartType, columnKey) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    const key = getCacheKey(chartType, columnKey);
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear specific chart cache:', error);
  }
};
